import { Formik } from 'formik';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getInitialDynamicFieldValues } from '../../utils/dynamicFields';
import { reportErrorToSentry } from '../../utils/initializeSentry';
import { getInitialRoute } from '../../utils/routing';
import { pushErrorTrackingEvent } from '../../utils/tracking';
import AuthForm from '../molecules/AuthForm';
import { showDefaultError, showNotification } from '../molecules/Notifications';

const AuthStep = () => {
  const { checkoutState, signIn, register } = useCheckoutState();
  const { formatMessage } = useIntl();
  const isAuthBasedRedirectEnabled = useRef(true);

  if (checkoutState.customer && isAuthBasedRedirectEnabled.current) {
    const basketLink = config.links.find(({ key }) => key === 'basket')?.link;
    if (basketLink) {
      window.location.href = basketLink;
      return null;
    } else {
      return <Navigate to={getInitialRoute(checkoutState)} replace />;
    }
  }

  if (checkoutState.customer) {
    return <Navigate to={getInitialRoute(checkoutState)} />;
  }

  const returningCustomer = window.ayCheckout?.customer;

  const initialValues = {
    type: 'signIn',
    gender: 'f',
    firstName: '',
    lastName: '',
    email: returningCustomer?.email ?? '',
    password: '',
    ...getInitialDynamicFieldValues(checkoutState, 'register', 'guest'),
  };

  const statusCodeI18nKeys = {
    signIn: {
      404: 'auth.notifications.notFound',
      405: 'auth.notifications.deactivated',
    },
    register: {
      409: 'auth.notifications.alreadyExists',
      412: 'auth.notifications.validationError',
    },
    guest: {},
  };

  const statusCodeTrackingEventAction = {
    signIn: {
      404: 'general form',
      405: 'general form',
    },
    register: {
      409: 'general form',
      412: 'input errors',
    },
  };

  const onSubmit = async ({
    type,
    gender,
    firstName,
    lastName,
    email,
    password,
    isNewsletterSignUpAccepted,
  }: typeof initialValues) => {
    isAuthBasedRedirectEnabled.current = false;
    try {
      if (type === 'signIn') {
        await signIn({ email, password });
      } else if (type === 'register') {
        await register({
          gender,
          firstName,
          lastName,
          email,
          password,
          hasSubscribedNewsletter: isNewsletterSignUpAccepted || false,
          source: '',
        });
      } else if (type === 'guest') {
        await register({
          gender,
          firstName,
          lastName,
          email,
          hasSubscribedNewsletter: isNewsletterSignUpAccepted || false,
          source: '',
        });
      }
    } catch (e: any) {
      const error = statusCodeI18nKeys[type][e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        pushErrorTrackingEvent(statusCodeTrackingEventAction[type][e.statusCode], 'error', message);

        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'auth-error',
        });
      } else {
        console.error(e);
        showDefaultError();
        reportErrorToSentry(e);
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      <AuthForm returningCustomer={returningCustomer} />
    </Formik>
  );
};

export default AuthStep;
